import React, { useState, useEffect } from 'react'
import {
  create,
  parseCreationOptionsFromJSON,
} from '@github/webauthn-json/browser-ponyfill'
import {
  finishRegisterUserWebAuthN,
  registerUserWebAuthN,
} from 'action_creators/user/userWebAuthN'
import Button from 'britive-design-system/core/components/button'
import DialogPopup from 'britive-design-system/core/components/dialog'
import ModalPopup from 'britive-design-system/core/components/modal-popup'
import Table from 'britive-design-system/core/components/table'
import Typography from 'britive-design-system/core/components/typography'
import Snackbar from 'britive-design-system/core/components/snackbar'
import isEmpty from 'lodash/isEmpty'

import { useDispatch, useSelector } from 'react-redux'
import totp from 'static/images/totp.svg'
import webAuthN from 'static/images/webAuthN.svg'
import toast from 'utils/toast'
import { cancelRegistrationWarning, mfaFactor, mfaStatus } from '../constants'
import './RegisterVerificationDevice.scss'
import TotpModelContent from './TotpModelContent'
import WebAuthNModelContent from './WebAuthNModelContent'
import { BsChevronUp, BsChevronDown } from 'react-icons/bs'
import { isMfaOptionEnabled } from 'routes/admin/Security/MFASettings/utils'
import CopyButton from 'components/CopyButton'

const RegisterVerificationDevice = ({
  mfaRegister,
  mfaValidate,
  onRegister,
  onVerify,
  onCancel,
  mfaSettings = [],
}) => {
  const dispatch = useDispatch()
  const { data } = useSelector(state => state.userWebAuthN?.register)

  const { data: mfaRegisteredDevices } = useSelector(state => state.user?.mfa)

  const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState(false)
  const [registrationError, setRegistrationError] = useState('')
  const [showAlphaNumericCode, setShowAlphaNumericCode] = useState(false)
  const [askConfirmationToCancel, setAskConfirmationToCancel] = useState(false)
  const [mfaOtp, setMfaOtp] = useState('')
  const [webAuthnName, setWebAuthnName] = useState('')
  const [webAuthnDescription, setWebAuthnDescription] = useState('')
  const [isWebAuthNRegistering, setIsWebAuthNRegistering] = useState(false)
  const [isExpanded, setIsExpanded] = useState(true)

  useEffect(() => {
    if (mfaValidate?.success) {
      setIsRegistrationModalOpen(false)
    }
  }, [JSON.stringify(mfaValidate)])

  const totpCredentials = mfaRegisteredDevices.filter(
    item => item.factor === mfaFactor.totp
  )[0]

  const webAuthNCredentials =
    mfaRegisteredDevices.filter(item => item.factor === mfaFactor.webAuthN)[0]
      ?.additionalDetails?.registeredCredentials || []

  const resetState = () => {
    setMfaOtp('')
  }

  const toggleRegistrationModal = () => {
    setIsRegistrationModalOpen(!isRegistrationModalOpen)
  }
  const onWebAuthNStartRegister = async () => {
    setIsWebAuthNRegistering(true)
    try {
      await dispatch(registerUserWebAuthN(mfaFactor.webAuthN))
      setIsRegistrationModalOpen(true)
      setRegistrationError('')
    } catch (error) {
      toast({
        title: error,
        type: 'error',
        time: 'normal',
      })
    }
  }

  const cleanupRegistrationState = () => {
    setIsRegistrationModalOpen(false)
    setIsWebAuthNRegistering(false)
    setWebAuthnName('')
    setWebAuthnDescription('')
    setRegistrationError('')
  }

  const onWebAuthNFinishRegister = async () => {
    if (isEmpty(data)) return
    setRegistrationError('')
    try {
      const options = parseCreationOptionsFromJSON(
        JSON.parse(data?.additionalDetails?.creationOptionsJson)
      )

      const authenticatorSelection = { residentKey: 'discouraged' }
      options.publicKey.authenticatorSelection = authenticatorSelection
      if (webAuthNCredentials.some(device => device.friendlyName === webAuthnName)) {
        setRegistrationError(`WebAuthn device name already exists.`)
        return
      }
      const publicKeyCredential = await create(options)
      if (!publicKeyCredential) return

      const creationOptionsId = data?.additionalDetails?.creationOptionsId
      await dispatch(
        finishRegisterUserWebAuthN(
          mfaFactor.webAuthN,
          creationOptionsId,
          publicKeyCredential,
          webAuthnName,
          webAuthnDescription
        )
      )

      toast({
        title: `WebAuthn device ${webAuthnName} registered successfully!`,
        type: 'success',
        time: 'normal',
      })
      cleanupRegistrationState()
    } catch (error) {
      handleRegistrationError(error)
    }
  }

  const handleRegistrationError = error => {
    switch (error.name) {
      case 'TimeoutError':
        toast({
          title: 'Registration took too long. Please try again.',
          type: 'error',
          time: 'normal',
        })
        break

      case 'InvalidStateError':
        setRegistrationError(`WebAuthn device is already registered.`)
        break

      case 'ConstraintError':
        setRegistrationError(`WebAuthn device name already exists.`)
        break

      default:
        if (
          error?.response?.status === 409 &&
          error?.response?.data?.errorCode === 'MFA-0006'
        ) {
          setIsRegistrationModalOpen(true)
          setIsWebAuthNRegistering(true)
          setRegistrationError(`WebAuthn device name already exists.`)
        } else {
          cleanupRegistrationState()
        }
    }
  }
  const toggleAlphanumericCodeModal = () => {
    setShowAlphaNumericCode(!showAlphaNumericCode)
  }

  const toggleAskConfirmationToCancel = () => {
    setWebAuthnName('')
    setWebAuthnDescription('')
    setAskConfirmationToCancel(!askConfirmationToCancel)
  }

  // Checking if the otp contains only numeric values.
  const isValidOtp = () => /^\d+$/.test(mfaOtp)

  return (
    <>
      <div className="register-mfa-devices">
        <div className="header">
          <h2 className="title">MFA Devices</h2>
        </div>
        <div className="mfa-options">
          <div className="mfa-otp">
            <div className="mfa-label">
              <img src={totp} alt="OTP" className="icon" />
              <p className="label">OTP</p>
            </div>
            <div className="mfa-button">
              {totpCredentials?.status === mfaStatus.registered ? (
                <Typography variant="label1"> Device Registered</Typography>
              ) : (
                <Button
                  disabled={!isMfaOptionEnabled(mfaSettings, mfaFactor.totp)}
                  onClick={() => {
                    onRegister()
                    toggleRegistrationModal()
                  }}
                >
                  Register
                </Button>
              )}
            </div>
          </div>
          <div className="mfa-webAuthn">
            {webAuthNCredentials.length !== 0 && (
              <div
                className="mfa-webAuthn-expand"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {isExpanded ? <BsChevronUp /> : <BsChevronDown />}
              </div>
            )}
            <div className="mfa-webAuthn-container">
              <div className="mfa-label">
                <img src={webAuthN} alt="WebAuthn" className="icon" />
                <p className="label">WebAuthn</p>
              </div>
              <div className="mfa-button">
                <Button
                  disabled={!isMfaOptionEnabled(mfaSettings, mfaFactor.webAuthN)}
                  onClick={onWebAuthNStartRegister}
                >
                  Register
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div className="webauthn-registrations">
          {isExpanded && webAuthNCredentials.length !== 0 && (
            <div className="device-details">
              <Table
                columns={[
                  {
                    field: 'friendlyName',
                    headerName: 'Device Name',
                    width: '250px',
                  },
                  {
                    headerName: 'Description',
                    field: 'description',
                  },
                ]}
                rows={webAuthNCredentials}
              />
            </div>
          )}
        </div>
      </div>
      {isRegistrationModalOpen && !mfaRegister.fetching && (
        <ModalPopup
          width={650}
          title={'Register your verification device'}
          buttons={[
            {
              text: 'Register',
              variant: 'primary',
              size: 'large',
              disabled: isWebAuthNRegistering
                ? webAuthnName === ''
                : mfaOtp?.length < 4 || !isValidOtp(),
              onClick: () => {
                isWebAuthNRegistering ? onWebAuthNFinishRegister() : onVerify(mfaOtp)
              },
            },
            {
              text: 'Cancel',
              variant: 'secondary',
              size: 'large',
              onClick: toggleAskConfirmationToCancel,
            },
          ]}
          onCancel={toggleAskConfirmationToCancel}
        >
          {isWebAuthNRegistering ? (
            <>
              {registrationError && (
                <>
                  <Snackbar errorList={[registrationError]} />
                  <br />
                </>
              )}
              <WebAuthNModelContent
                webAuthnName={webAuthnName}
                setWebAuthnName={setWebAuthnName}
                webAuthnDescription={webAuthnDescription}
                setWebAuthnDescription={setWebAuthnDescription}
              />
            </>
          ) : (
            <TotpModelContent
              mfaValidate={mfaValidate}
              mfaRegister={mfaRegister}
              toggleAlphanumericCodeModal={toggleAlphanumericCodeModal}
              mfaOtp={mfaOtp}
              setMfaOtp={setMfaOtp}
              isValidOtp={isValidOtp}
            />
          )}
        </ModalPopup>
      )}
      {showAlphaNumericCode && (
        <DialogPopup
          type="general"
          title={'Please use the code below'}
          message={
            <div className="mfa-secret">
              <Typography variant="label2">
                {mfaRegister?.data?.additionalDetails?.key}
              </Typography>
              <CopyButton textToCopy={mfaRegister?.data?.additionalDetails?.key} />
            </div>
          }
          primaryButtonText={'Done'}
          onSubmit={toggleAlphanumericCodeModal}
          onCancel={toggleAlphanumericCodeModal}
        />
      )}
      {askConfirmationToCancel && (
        <DialogPopup
          type="alert"
          title={'Are you sure?'}
          message={cancelRegistrationWarning}
          primaryButtonText={'Yes, Cancel'}
          secondaryButtonText={'No'}
          onCancel={toggleAskConfirmationToCancel}
          onSubmit={() => {
            onCancel()
            toggleAskConfirmationToCancel()
            toggleRegistrationModal()
            resetState()
          }}
        />
      )}
    </>
  )
}

export default RegisterVerificationDevice
